// Defines the only bounded diagnostic fields a toast may show, matching the server's ToastDetails.

export type ToastDetails = {
  reference?: string
  provider?: string
  category?: string
  status?: number
}

export type ToastDetailLabels = Record<keyof ToastDetails, string>

const TEXT_LIMITS = { reference: 96, provider: 32, category: 32 } as const
const TOKEN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/

/**
 * Returns the allowed details, skipping null and undefined values, or undefined when none remain;
 * throws on any other field or an unsafe value.
 */
export function parseToastDetails(value: unknown): ToastDetails | undefined {
  if (value === undefined || value === null) return undefined

  if (typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Toast details must be a dictionary.')
  }

  const details: ToastDetails = {}

  for (const [field, item] of Object.entries(value)) {
    if (field !== 'status' && !Object.hasOwn(TEXT_LIMITS, field)) {
      throw new Error('Toast details contain an unsupported field.')
    }

    if (item === undefined || item === null) continue

    if (field === 'status') {
      if (typeof item !== 'number' || !Number.isInteger(item) || item < 100 || item > 599) {
        throw new Error('Toast detail status must be an HTTP status.')
      }

      details.status = item
      continue
    }

    const textField = field as keyof typeof TEXT_LIMITS

    if (typeof item !== 'string' || item.length > TEXT_LIMITS[textField] || !TOKEN.test(item)) {
      throw new Error(`Toast detail ${field} is not a safe token.`)
    }

    details[textField] = item
  }

  return Object.keys(details).length === 0 ? undefined : details
}

/** Returns the localized field labels the layout renders on <body>. */
export function toastDetailLabels(): ToastDetailLabels {
  const data = document.body?.dataset

  return {
    reference: data?.toastDetailReferenceLabel || 'Reference',
    provider: data?.toastDetailProviderLabel || 'Provider',
    category: data?.toastDetailCategoryLabel || 'Category',
    status: data?.toastDetailStatusLabel || 'Status',
  }
}
